import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Row,
  Spinner,
} from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../../../components/toast/ToastProvider';
import useAdminAmenities from '../../hooks/useAdminAmenities';
import { Controller } from 'react-hook-form';
import Select from 'react-select';
import {
  PropertyToAmenities,
  PropertyToCharacteristics,
} from '../../models/propertyModel';
import { Option } from '../../../../models/formModel';
import {
  TablerIconPicker,
  iconOptionLabel,
} from '../../../../components/TablerIcon';
import {
  getCurrentAmenitiesValues,
  getCurrentFeatureIds,
  getExtraCharacteristics,
} from '../../adapters/propertyAdapter';

export interface Props {
  property_id: string;
  has_white_goods: boolean;
  is_furnished: boolean;
  pets_allowed: boolean;
  amenities_info: PropertyToAmenities[];
  characteristics_info: PropertyToCharacteristics[];
  advantages: string[];
  bathrooms: string;
  parkings: string;
  rooms: string;
  half_bathrooms: string;
  square_mts: number;
}

const SectionTitle = ({
  icon,
  title,
  hint,
  first = false,
}: {
  icon: string;
  title: string;
  hint?: string;
  first?: boolean;
}) => (
  <div
    className="mb-3 border-bottom pb-2"
    style={{ marginTop: first ? '1.5rem' : '2.5rem' }}
  >
    <h5 className="text-uppercase text-muted ls-1 mb-1">
      <i className={`${icon} fa-fw mr-2`}></i>
      {title}
    </h5>
    {hint && <small className="text-muted">{hint}</small>}
  </div>
);

const numberList: Option[] = [
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4' },
  { value: '5', label: '+5' },
];

interface NewItemValues {
  description: string;
  feature_group: string;
  icon: string;
}

/** Link "+ Crear nueva …" que abre un mini-formulario dentro de la sección. */
const NewCatalogItem = ({
  linkText,
  showGroup,
  requireIcon = false,
  isLoading,
  onCreate,
}: {
  linkText: string;
  showGroup: boolean;
  // la columna amenity.icon es NOT NULL en la base de datos
  requireIcon?: boolean;
  isLoading: boolean;
  onCreate: (values: NewItemValues) => Promise<boolean>;
}) => {
  const empty = { description: '', feature_group: '', icon: '' };
  const [isOpen, setIsOpen] = useState(false);
  const [values, setValues] = useState<NewItemValues>(empty);

  const create = async () => {
    if (!values.description.trim()) return;
    if (await onCreate(values)) {
      setValues(empty);
      setIsOpen(false);
    }
  };

  if (!isOpen) {
    return (
      <Button
        type="button"
        color="link"
        size="sm"
        className="px-0"
        onClick={() => setIsOpen(true)}
      >
        <i className="fa-solid fa-plus mr-1" />
        {linkText}
      </Button>
    );
  }

  return (
    <div className="bg-secondary rounded p-3 mt-2">
      <Row>
        <Col md={showGroup ? 4 : 6}>
          <Input
            type="text"
            maxLength={60}
            autoFocus
            aria-label="Nombre"
            placeholder="Nombre. Ej: Terraza"
            className="text-dark"
            value={values.description}
            onChange={(e) =>
              setValues({ ...values, description: e.target.value })
            }
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              e.preventDefault();
              create();
            }}
          />
        </Col>
        {showGroup && (
          <Col md={3} className="mt-2 mt-md-0">
            <Input
              type="select"
              aria-label="Grupo"
              className="text-dark"
              value={values.feature_group}
              onChange={(e) =>
                setValues({ ...values, feature_group: e.target.value })
              }
            >
              <option value="">Sin grupo</option>
              <option value="interior">Interior</option>
              <option value="exterior">Exterior</option>
            </Input>
          </Col>
        )}
        <Col md={showGroup ? 3 : 4} className="mt-2 mt-md-0">
          <TablerIconPicker
            value={values.icon}
            placeholder={requireIcon ? 'Ícono (obligatorio)' : 'Ícono'}
            onChange={(icon) => setValues({ ...values, icon })}
          />
        </Col>
        <Col md={showGroup ? 2 : 2} className="mt-2 mt-md-0">
          <Button
            type="button"
            color="primary"
            block
            onClick={create}
            disabled={
              isLoading ||
              !values.description.trim() ||
              (requireIcon && !values.icon)
            }
          >
            Crear
          </Button>
        </Col>
      </Row>
      <Button
        type="button"
        color="link"
        size="sm"
        className="px-0 mt-2 text-muted"
        onClick={() => setIsOpen(false)}
      >
        Cancelar
      </Button>
    </div>
  );
};

const PropertyAmenities: React.FC<Props> = ({
  has_white_goods,
  is_furnished,
  pets_allowed,
  property_id,
  amenities_info,
  characteristics_info,
  advantages: initialAdvantages,
  bathrooms,
  parkings,
  rooms,
  half_bathrooms,
  square_mts,
}) => {
  const navigate = useNavigate();
  const toast = useToast();
  const {
    control,
    setValue,
    reset,
    handleSubmit,
    isLoading,
    amenitiesCatalog,
    currentAmenities,
    characteristicsCatalog,
    updatePropertyAmenities,
    createFeatureCharacteristic,
    createAmenity,
  } = useAdminAmenities(property_id, amenities_info, characteristics_info);

  // Cambios sin guardar: avisa al cerrar la pestaña o al cancelar.
  const [isDirty, setIsDirty] = useState(false);
  const markDirty = () => setIsDirty(true);
  useEffect(() => {
    if (!isDirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [isDirty]);

  const cancel = () => {
    if (
      isDirty &&
      !window.confirm('Tienes cambios sin guardar. ¿Salir de todas formas?')
    ) {
      return;
    }
    navigate(`/properties/admin/${property_id}`);
  };

  // Características extra (terraza, bodega…): multiselect como las amenidades.
  const [featureIds, setFeatureIds] = useState<string[]>(
    getCurrentFeatureIds(characteristics_info),
  );
  const featureGroupLabels = { interior: 'Interior', exterior: 'Exterior' };
  const featureOptions = getExtraCharacteristics(characteristicsCatalog).map(
    ({ id, description, feature_group, icon }) => ({
      value: id,
      label: feature_group
        ? `${description} (${featureGroupLabels[feature_group]})`
        : description,
      icon,
    }),
  );
  // Las ya seleccionadas se muestran aunque el catálogo no haya cargado todavía.
  const selectedFeatures = featureIds.map(
    (id) =>
      featureOptions.find(({ value }) => value === id) ?? {
        value: id,
        label:
          characteristics_info.find(({ characteristic }) => characteristic.id === id)
            ?.characteristic.description ?? id,
        icon: characteristics_info.find(
          ({ characteristic }) => characteristic.id === id,
        )?.characteristic.icon,
      },
  );

  const selectFeatureIds = (ids: string[]) => {
    setFeatureIds(ids);
    setValue('features_info', ids);
    markDirty();
  };

  const addFeatureToCatalog = async ({
    description,
    feature_group,
    icon,
  }: NewItemValues) => {
    const created = await createFeatureCharacteristic({
      description: description.trim(),
      ...(feature_group && {
        feature_group: feature_group as 'interior' | 'exterior',
      }),
      ...(icon.trim() && { icon: icon.trim() }),
    });
    if (!created) return false;
    selectFeatureIds(
      featureIds.includes(created.id) ? featureIds : [...featureIds, created.id],
    );
    toast.success(`Característica "${created.description}" agregada.`);
    return true;
  };

  // Amenidades seleccionadas (controladas, para poder agregar una nueva).
  const [amenityOptions, setAmenityOptions] = useState<any[]>([]);
  useEffect(() => {
    setAmenityOptions(currentAmenities);
  }, [currentAmenities]);

  const selectAmenities = (selected: any[]) => {
    setAmenityOptions(selected);
    setValue(
      'amenities_info',
      selected.map(({ value }) => value),
    );
    markDirty();
  };

  const addAmenityToCatalog = async ({ description, icon }: NewItemValues) => {
    const name = description.trim();
    const existing = amenitiesCatalog.find(
      ({ label }) => label.toLowerCase() === name.toLowerCase(),
    );
    const option =
      existing ??
      (await createAmenity({
        description: name,
        is_enabled: true,
        ...(icon.trim() && { icon: icon.trim() }),
      }));
    if (!option) return false;
    if (!amenityOptions.some(({ value }) => value === option.value)) {
      selectAmenities([...amenityOptions, option]);
    }
    toast.success(
      existing
        ? `"${option.label}" ya existía; se seleccionó.`
        : `Amenidad "${option.label}" agregada.`,
    );
    return true;
  };

  // Ventajas en texto libre: se agregan de una en una.
  const [advantages, setAdvantages] = useState<string[]>(initialAdvantages);
  const [newAdvantage, setNewAdvantage] = useState('');

  const updateAdvantages = (next: string[]) => {
    setAdvantages(next);
    setValue('advantages', next);
    markDirty();
  };

  const addAdvantage = () => {
    const text = newAdvantage.trim();
    if (!text || advantages.includes(text)) return;
    updateAdvantages([...advantages, text]);
    setNewAdvantage('');
  };

  useEffect(() => {
    reset({
      has_white_goods: !!has_white_goods,
      is_furnished: !!is_furnished,
      pets_allowed: !!pets_allowed,
      c_bathrooms: bathrooms,
      c_parkings: parkings,
      c_rooms: rooms,
      c_half_bathrooms: half_bathrooms,
      c_square_mts: square_mts || 0,
      amenities_info: getCurrentAmenitiesValues(amenities_info),
      features_info: getCurrentFeatureIds(characteristics_info),
      advantages: initialAdvantages,
    });
  }, []);

  const yesNoFields = [
    { name: 'has_white_goods', label: 'Línea blanca' },
    { name: 'is_furnished', label: 'Amueblado' },
    { name: 'pets_allowed', label: 'Mascotas' },
  ] as const;

  const numberFields = [
    { name: 'c_rooms', label: 'Habitaciones', empty: '' },
    { name: 'c_bathrooms', label: 'Baños completos', empty: '' },
    { name: 'c_half_bathrooms', label: 'Baños sociales', empty: '0' },
    { name: 'c_parkings', label: 'Estacionamientos', empty: '' },
  ] as const;

  return (
    <Card>
      <CardHeader>
        <h3 className="mb-0">Amenidades y características</h3>
        <small className="text-muted">
          Define lo que incluye la propiedad y lo que la hace destacar.
        </small>
      </CardHeader>
      <CardBody>
        <Form
          role="form"
          onChange={markDirty}
          onSubmit={handleSubmit(updatePropertyAmenities)}
        >
          <SectionTitle
            first
            icon="fa-solid fa-house"
            title="Características"
            hint="Lo que es parte de la propiedad en sí, sea casa, apartamento o terreno."
          />
          <Row>
            {yesNoFields.map(({ name, label }) => (
              <Col md={4} key={name}>
                <FormGroup className="d-flex align-items-center justify-content-between">
                  <label className="form-control-label mb-0" htmlFor={name}>
                    {label}
                  </label>
                  <Controller
                    name={name}
                    control={control}
                    render={({ field }) => (
                      <label className="custom-toggle mb-0">
                        <input
                          id={name}
                          type="checkbox"
                          checked={!!field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                        <span
                          className="custom-toggle-slider rounded-circle"
                          data-label-off="No"
                          data-label-on="Sí"
                        />
                      </label>
                    )}
                  />
                </FormGroup>
              </Col>
            ))}
          </Row>

          {/* `lg` sin número: las 5 columnas se reparten el ancho y quedan en una sola fila. */}
          <Row>
            {numberFields.map(({ name, label, empty }) => (
              <Col md={6} lg key={name}>
                <FormGroup>
                  <label className="form-control-label" htmlFor={name}>
                    {label}
                  </label>
                  <Controller
                    name={name}
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        id={name}
                        type="select"
                        className="text-dark"
                      >
                        <option value={empty}>No posee...</option>
                        {numberList.map(({ label, value }) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </Input>
                    )}
                  />
                </FormGroup>
              </Col>
            ))}
            <Col md={6} lg>
              <FormGroup>
                <label className="form-control-label" htmlFor="c_square_mts">
                  Metros cuadrados
                </label>
                <Controller
                  name="c_square_mts"
                  control={control}
                  render={({ field }) => (
                    <InputGroup>
                      <Input
                        {...field}
                        id="c_square_mts"
                        type="number"
                        min={0}
                        className="text-dark"
                      />
                      <InputGroupAddon addonType="append">
                        <InputGroupText>m²</InputGroupText>
                      </InputGroupAddon>
                    </InputGroup>
                  )}
                />
              </FormGroup>
            </Col>
          </Row>

          <FormGroup className="mb-0">
            <label className="form-control-label" htmlFor="features_info">
              Características de la propiedad
            </label>
            <small className="text-muted d-block mb-2">
              Ej: terraza, cocina equipada, bodega propia, cisterna, jardín,
              piscina privada, parqueo propio.
            </small>
            <Select
              inputId="features_info"
              placeholder="Selecciona las características"
              isMulti
              options={featureOptions}
              formatOptionLabel={iconOptionLabel}
              value={selectedFeatures}
              onChange={(selected) =>
                selectFeatureIds(selected.map(({ value }) => value))
              }
            />
          </FormGroup>
          <NewCatalogItem
            linkText="Crear nueva característica"
            showGroup
            isLoading={isLoading}
            onCreate={addFeatureToCatalog}
          />

          <SectionTitle
            icon="fa-solid fa-swimming-pool"
            title="Amenidades"
            hint="Servicios o instalaciones extra, casi siempre compartidas (del edificio, residencial o zona). Ej: piscina comunal, gimnasio, vigilancia 24/7, casa club, parqueo de visitas."
          />
          <FormGroup className="mb-0">
            {!isLoading && (
              <Select
                aria-label="Amenidades"
                placeholder="Selecciona las amenidades"
                isMulti
                options={amenitiesCatalog}
                formatOptionLabel={iconOptionLabel}
                value={amenityOptions}
                onChange={(selected) => selectAmenities([...selected])}
              />
            )}
          </FormGroup>
          <NewCatalogItem
            linkText="Crear nueva amenidad"
            showGroup={false}
            requireIcon
            isLoading={isLoading}
            onCreate={addAmenityToCatalog}
          />

          <SectionTitle
            icon="fa-solid fa-star"
            title="Ventajas"
            hint="Puntos fuertes para vender o rentar. Ej: apta para crédito bancario, entrega inmediata."
          />
          <Row>
            <Col md={9}>
              <Input
                id="new_advantage"
                aria-label="Ventajas"
                type="text"
                maxLength={200}
                className="text-dark"
                placeholder="Ej: Apta para crédito bancario"
                value={newAdvantage}
                onChange={(e) => setNewAdvantage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') return;
                  // Enter agrega la ventaja, no envía el formulario
                  e.preventDefault();
                  addAdvantage();
                }}
              />
            </Col>
            <Col md={3} className="mt-2 mt-md-0">
              <Button
                type="button"
                color="primary"
                outline
                block
                onClick={addAdvantage}
                disabled={!newAdvantage.trim()}
              >
                Agregar
              </Button>
            </Col>
          </Row>
          {advantages.length > 0 && (
            <div className="mt-3">
              {advantages.map((advantage) => (
                <span
                  key={advantage}
                  className="badge badge-secondary d-inline-flex align-items-center mr-2 mb-2 py-2 px-3 text-dark"
                  style={{
                    fontSize: '0.85rem',
                    textTransform: 'none',
                    fontWeight: 500,
                  }}
                >
                  {advantage}
                  <button
                    type="button"
                    className="close ml-2"
                    aria-label={`Quitar ${advantage}`}
                    style={{ fontSize: '1.1rem', lineHeight: 1 }}
                    onClick={() =>
                      updateAdvantages(
                        advantages.filter((item) => item !== advantage),
                      )
                    }
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                </span>
              ))}
            </div>
          )}

          <div className="text-right border-top pt-3 mt-5">
            <Button
              type="button"
              color="secondary"
              outline
              className="mr-2"
              onClick={cancel}
            >
              Cancelar
            </Button>
            <Button color="primary" disabled={isLoading}>
              {isLoading && <Spinner size="sm" className="mr-2" />}
              Actualizar
            </Button>
          </div>
        </Form>
      </CardBody>
    </Card>
  );
};

export default PropertyAmenities;
